/**
 * reCAPTCHA v2 invisible para la consulta de cédula a People Service.
 *
 * People Service exige el header `X-Recaptcha-Token` en el endpoint de consulta
 * de cédula para frenar consultas automatizadas masivas. El token es de un solo
 * uso y expira a los ~2 minutos, así que se ejecuta el challenge en cada
 * consulta y nunca se cachea ni se reutiliza entre reintentos.
 *
 * El script de Google y el widget se cargan/renderizan una sola vez (a demanda,
 * en la primera consulta) sobre el contenedor `#recaptcha-container` de
 * index.html. Las ejecuciones posteriores reutilizan ese mismo widget.
 */

const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js?render=explicit';
const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';
const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

// Estado a nivel de módulo: el widget es único para toda la app.
let scriptPromise = null;
let widgetIdPromise = null;

// Callbacks de la ejecución en curso. `grecaptcha` sólo permite un challenge
// a la vez por widget, así que se serializan las ejecuciones concurrentes.
let pendingExecution = null;

const getSiteKey = () => import.meta.env.VITE_RECAPTCHA_SITE_KEY;

/**
 * Inyecta el script de Google una sola vez.
 */
const loadScript = () => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha?.render) {
      resolve();
      return;
    }

    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('No se pudo cargar reCAPTCHA.')));
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = RECAPTCHA_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Permite reintentar la carga en una consulta posterior.
      scriptPromise = null;
      script.remove();
      reject(new Error('No se pudo cargar reCAPTCHA.'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Espera a que `grecaptcha.render` esté disponible tras cargar el script.
 */
const waitForGrecaptcha = () => new Promise((resolve, reject) => {
  if (window.grecaptcha?.render) {
    resolve();
    return;
  }
  // `grecaptcha.ready` existe desde que el script se evalúa, pero por si acaso
  // se hace polling corto como respaldo.
  if (window.grecaptcha?.ready) {
    window.grecaptcha.ready(() => resolve());
    return;
  }

  const startedAt = Date.now();
  const timer = setInterval(() => {
    if (window.grecaptcha?.render) {
      clearInterval(timer);
      resolve();
    } else if (Date.now() - startedAt > 10000) {
      clearInterval(timer);
      reject(new Error('reCAPTCHA no se inicializó a tiempo.'));
    }
  }, 100);
});

/**
 * Devuelve (creándolo si hace falta) el contenedor oculto del widget.
 */
const getContainer = () => {
  let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = RECAPTCHA_CONTAINER_ID;
    document.body.appendChild(container);
  }
  return container;
};

/**
 * Renderiza el widget invisible una sola vez y devuelve su widgetId.
 */
const getWidgetId = () => {
  if (widgetIdPromise) return widgetIdPromise;

  widgetIdPromise = (async () => {
    const sitekey = getSiteKey();
    if (!sitekey) {
      throw new Error('VITE_RECAPTCHA_SITE_KEY no está configurada.');
    }

    await loadScript();
    await waitForGrecaptcha();

    return window.grecaptcha.render(getContainer(), {
      sitekey,
      size: 'invisible',
      callback: (token) => {
        pendingExecution?.resolve(token);
      },
      'error-callback': () => {
        pendingExecution?.reject(new Error('reCAPTCHA falló al generar el token.'));
      },
      'expired-callback': () => {
        pendingExecution?.reject(new Error('El challenge de reCAPTCHA expiró.'));
      },
    });
  })().catch((error) => {
    // No memoizar el fallo: permite reintentar en la siguiente consulta.
    widgetIdPromise = null;
    throw error;
  });

  return widgetIdPromise;
};

/**
 * Ejecuta el challenge y resuelve con un token fresco de un solo uso.
 *
 * Debe llamarse al momento de disparar la consulta (nunca antes), porque el
 * token expira a los ~2 minutos.
 *
 * @returns {Promise<string>} token para el header `X-Recaptcha-Token`
 */
export const executeRecaptcha = async () => {
  const widgetId = await getWidgetId();

  // Serializa: si hay un challenge en vuelo, se espera a que termine.
  const previous = pendingExecution?.promise;
  if (previous) {
    await previous.catch(() => {});
  }

  // `reset` descarta cualquier token previo del widget para forzar uno nuevo.
  window.grecaptcha.reset(widgetId);

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  pendingExecution = { promise, resolve, reject };

  const timeout = setTimeout(
    () => reject(new Error('reCAPTCHA no respondió a tiempo.')),
    30000,
  );

  try {
    window.grecaptcha.execute(widgetId);
    return await promise;
  } finally {
    clearTimeout(timeout);
    pendingExecution = null;
  }
};

export default function useRecaptcha() {
  return { executeRecaptcha };
}
